import { ICellRendererParams } from "ag-grid-enterprise";
import { DateUtils } from "utils/dateutils";
import { sortAlphanumeric, sortLinkedItems } from "utils/utils";
import { FeatureTypes } from "entity-app/constants";
import { EWorkflowStatusDataBlock } from "constants/index";
import { COMMENT_LABEL_PATTERN } from "utils/comments-export";

const PRINT_RISK_HIGH = "HIGH";
const PRINT_RISK_HIGH_COLOR = "#E2381F";
const MAX_VISIBLE_ITEMS = 1;

type TLinkedSubmittal = {
  id: string;
  feature_type: number;
  sequence_id: number;
  name: string;
  milestone_state: string;
  risk_level?: string;
  workflow_status: EWorkflowStatusDataBlock;
  deleted_from_source: boolean;
  type: any;
  revision?: number;
  spec_section: Array<{
    id: string;
    name: string | null;
    number: string | null;
  }>;
  milestones: Array<{
    actual_date: string | null;
    name: string | null;
    projected_date: string | null;
    planned_date: string | null;
    sequence_no: number;
    milestone_action: string | null;
    milestone_type: {
      id: number;
      name: string;
    };
  }>;
};

const sortFeatureSubmittalLinkedItems = (items: Array<TLinkedSubmittal>) => {
  const riskOrder: Record<string, number> = {
    HIGH: 3,
    MEDIUM: 2,
    LOW: 1,
    blank: 0
  };
  return [...items].sort((a, b) => {
    const aVoid = a.workflow_status === EWorkflowStatusDataBlock.VOID;
    const bVoid = b.workflow_status === EWorkflowStatusDataBlock.VOID;

    if (aVoid && !bVoid) return -1;
    if (!aVoid && bVoid) return 1;

    const riskDiff =
      riskOrder[b.risk_level ?? "blank"] - riskOrder[a.risk_level ?? "blank"];
    if (riskDiff !== 0) return riskDiff;

    return a.sequence_id - b.sequence_id;
  });
};

/**
 * Print version of TaskLinksCellRenderer.
 * Shows first 2 tasks as plain text, then "+N More" if there are extras.
 */
export function PrintTaskLinksCellRenderer(params: ICellRendererParams) {
  const { data } = params;
  const tasks = sortAlphanumeric(data?.tasks || [], "activity_id");

  if (!tasks.length) return null;

  const visible = tasks.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = tasks.length - MAX_VISIBLE_ITEMS;

  return (
    <div className="flex flex-col gap-y-0.5 text-xs py-0.5 ">
      {visible.map((task: any) => {
        const name = task.activity_id
          ? `${task.activity_id} - ${task.task_name}`
          : task.task_name;
        return (
          <div key={task?.task_id} className="break-words whitespace-normal">
            {name}
          </div>
        );
      })}
      {remainingCount > 0 && (
        <div className="text-[11px] text-gray-500">+{remainingCount} More</div>
      )}
    </div>
  );
}

/**
 * Print version of MaterialLinksCellRenderer.
 * Shows first 2 materials as plain text with red text for high risk, then "+N More".
 */
export function PrintMaterialLinksCellRenderer(params: ICellRendererParams) {
  const { data } = params;
  const value = data?.materials?.filter((m: any) => !m.implicit);
  const materials = sortLinkedItems(value || [], "material_sequence_id");
  const actualMaterials = materials.filter(
    (material: any) => !material.implicit
  );

  if (!actualMaterials.length) return null;

  const visible = actualMaterials.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = actualMaterials.length - MAX_VISIBLE_ITEMS;
  const hasHighRiskRemaining = actualMaterials
    .slice(MAX_VISIBLE_ITEMS)
    .some((m: any) => m.risk_level === PRINT_RISK_HIGH);

  return (
    <div className="flex flex-col gap-y-0.5 text-xs py-0.5 ">
      {visible.map((material: any) => {
        const name = material.implicit
          ? // implicit materials are filtered out above (!m.implicit / !material.implicit) so this branch is never taken
            /* v8 ignore next */
            material.name
          : `${material?.material_sequence_id} - ${material?.name}`;
        const isHighRisk = material.risk_level === PRINT_RISK_HIGH;
        return (
          <div
            key={material.id}
            className="break-words whitespace-normal"
            style={isHighRisk ? { color: PRINT_RISK_HIGH_COLOR } : undefined}
          >
            {name}
          </div>
        );
      })}
      {remainingCount > 0 && (
        <div
          className="text-[11px]"
          style={
            hasHighRiskRemaining
              ? { color: PRINT_RISK_HIGH_COLOR }
              : { color: "#6b7280" }
          }
        >
          +{remainingCount} More
        </div>
      )}
    </div>
  );
}

/**
 * Print version of SubmittalLinksEntityCellRenderer.
 * Shows first 2 submittals with Submitted/Approved dates, red text for high risk,
 * then "+N More".
 */
export function PrintSubmittalLinksEntityCellRenderer(
  params: ICellRendererParams
) {
  const { data, context } = params;
  const showSpecSectionNo = context?.projectDetails?.spec_section;

  const linkedItems = (data?.linked_items || []) as Array<TLinkedSubmittal>;

  const submittals = sortFeatureSubmittalLinkedItems(
    linkedItems?.filter((x: TLinkedSubmittal) => x.feature_type === 1)
  );

  // Handle planning-only submittals when no real submittals exist
  if (submittals.length === 0) {
    const plannedSubmittal = linkedItems?.find(
      (x: TLinkedSubmittal) =>
        x.feature_type === (FeatureTypes.PLANNING_ONLY_SUBMITTAL as number)
    );
    if (plannedSubmittal) {
      const submittedMilestone = plannedSubmittal.milestones?.find(
        (m) => m.milestone_type?.name === "Submission"
      );
      const approvedMilestone = plannedSubmittal.milestones?.find(
        (m) => m.milestone_type?.name === "Design Review"
      );
      return (
        <div className="text-xs py-0.5 ">
          <div className="font-semibold">Submittal Plan</div>
          <div>
            Submitted:{" "}
            {submittedMilestone?.planned_date
              ? DateUtils.format(submittedMilestone.planned_date)
              : "-"}
          </div>
          <div>
            Approved:{" "}
            {approvedMilestone?.planned_date
              ? DateUtils.format(approvedMilestone.planned_date)
              : "-"}
          </div>
        </div>
      );
    }
    return null;
  }

  const visible = submittals.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = submittals.length - MAX_VISIBLE_ITEMS;
  const hasHighRiskRemaining = submittals
    .slice(MAX_VISIBLE_ITEMS)
    .some((s) => s.risk_level === PRINT_RISK_HIGH);

  return (
    <div className="flex flex-col gap-y-1 text-xs py-0.5 ">
      {visible.map((submittal: TLinkedSubmittal) => {
        const sequenceId = submittal?.sequence_id || "";
        const specSection = submittal.spec_section?.length
          ? submittal.spec_section[0]
          : null;

        const submittalRefId: string =
          showSpecSectionNo && specSection
            ? `${specSection.number} - ${sequenceId}`
            : `${sequenceId}`;
        const name = `${submittalRefId} - ${submittal?.name}`;

        const submittalWFStarted =
          submittal?.workflow_status === EWorkflowStatusDataBlock.IN_PROGRESS ||
          submittal?.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED;

        const isVoid =
          submittal.workflow_status === EWorkflowStatusDataBlock.VOID;
        const isDeleted = submittal.deleted_from_source;
        const isHighRisk = submittal.risk_level === PRINT_RISK_HIGH;

        const subcontractorMilestone = submittal.milestones?.find(
          (x) => x.sequence_no === 2
        );
        const designReviewMilestone = submittal.milestones?.find(
          (x) => x.sequence_no === 4
        );

        const submitDate =
          subcontractorMilestone?.actual_date ||
          (submittalWFStarted
            ? subcontractorMilestone?.projected_date
            : subcontractorMilestone?.planned_date);

        const approvedDate =
          designReviewMilestone?.actual_date ||
          (submittalWFStarted
            ? designReviewMilestone?.projected_date
            : designReviewMilestone?.planned_date);

        return (
          <div key={submittal.id} className="break-words whitespace-normal">
            <div
              className="font-semibold"
              style={
                isHighRisk && !isVoid && !isDeleted
                  ? { color: PRINT_RISK_HIGH_COLOR }
                  : undefined
              }
            >
              {isDeleted && (
                <span className="text-black text-opacity-50 mr-1">Deleted</span>
              )}
              {isVoid && (
                <span className="text-black text-opacity-50 mr-1">Void</span>
              )}
              {name}
            </div>
            {!isVoid && !isDeleted && (
              <div className="text-[11px]">
                <span>
                  Submitted:{" "}
                  {submitDate ? DateUtils.format(submitDate, "MM-DD-YY") : "-"}
                </span>
                <span className="ml-2">
                  Approved:{" "}
                  {approvedDate
                    ? DateUtils.format(approvedDate, "MM-DD-YY")
                    : "-"}
                </span>
              </div>
            )}
          </div>
        );
      })}
      {remainingCount > 0 && (
        <div
          className="text-[11px]"
          style={
            hasHighRiskRemaining
              ? { color: PRINT_RISK_HIGH_COLOR }
              : { color: "#6b7280" }
          }
        >
          +{remainingCount} More
        </div>
      )}
    </div>
  );
}

/**
 * Print version of the Comments column: bolds each comment's author/date label.
 */
export function PrintCommentsCellRenderer(params: ICellRendererParams) {
  const value = params.value as string | null | undefined;
  if (!value) return null;

  // A comment can span several lines; only a labelled line starts a new one.
  const comments: { label?: string; text: string }[] = [];
  value.split("\n").forEach((line) => {
    const match = COMMENT_LABEL_PATTERN.exec(line);
    const current = comments.at(-1);
    if (match) {
      comments.push({ label: match[1], text: match[2] });
    } else if (current) {
      current.text += `\n${line}`;
    } else {
      comments.push({ text: line });
    }
  });

  return (
    <div className="flex flex-col gap-1.5 py-2 whitespace-pre-line break-words leading-[18px]">
      {comments.map((comment, index) => (
        <div key={index}>
          {comment.label && <strong>{comment.label}</strong>}
          {comment.text}
        </div>
      ))}
    </div>
  );
}

/**
 * Print version of FeatureMaterialLinksCellRenderer.
 * Transforms linked_items to materials format, then delegates to PrintMaterialLinksCellRenderer.
 */
export function PrintFeatureMaterialLinksCellRenderer(
  params: ICellRendererParams
) {
  const { data } = params;

  // Materials page uses field="materials" and stores data directly in data.materials.
  // Entity/submittal pages use linked_items which needs transformation.
  if (params.colDef?.field === "materials") {
    return PrintMaterialLinksCellRenderer(params);
  }

  const rendererParams = {
    ...params,
    data: {
      materials: (data?.linked_items || [])
        .filter(
          (item: any) =>
            item.feature_type === FeatureTypes.MATERIALS && !item.implicit
        )
        .map((item: any) => ({
          ...item,
          material_sequence_id: item.sequence_id,
          spec_section_no: item.spec_section?.length
            ? item.spec_section[0].number
            : "",
          spec_section_name: item.spec_section?.length
            ? item.spec_section[0].name
            : ""
        }))
    }
  };

  return PrintMaterialLinksCellRenderer(rendererParams);
}
